import { Logo, ScrollArea } from "@cynthai/ui";
import { useReducer, type FocusEvent } from "react";
import { twMerge } from "tailwind-merge";

import { SidebarItem } from "@/app/layout/Sidebar/SidebarItem";
import { SidebarProfile } from "@/app/layout/Sidebar/SidebarProfile";
import { useCurrentUser } from "@/core/hooks/use-current-user";
import type { SidebarItem as SidebarItemType } from "@/core/lib/sidebar-types";

type SidebarProps = {
  className?: string;
  menuItems: SidebarItemType[];
};

type SidebarSensor = "pointer" | "focus" | "menu";

type SidebarState = Record<SidebarSensor, boolean> & { isDismissed: boolean };

type SidebarEvent =
  | { type: SidebarSensor; isActive: boolean }
  | { type: "dismiss" };

const INITIAL_STATE: SidebarState = {
  pointer: false,
  focus: false,
  menu: false,
  isDismissed: false,
};

const isEngaged = ({ pointer, focus, menu }: SidebarState) =>
  pointer || focus || menu;

function sidebarReducer(
  state: SidebarState,
  event: SidebarEvent,
): SidebarState {
  if (event.type === "dismiss") {
    return { ...state, menu: false, isDismissed: true };
  }

  const next: SidebarState = { ...state, [event.type]: event.isActive };
  // the latch silences a single engagement: an explicit re-engagement lifts it,
  // and so does everything going quiet. focus does not count as one because it
  // stays parked on whichever item was just used to navigate
  const isReengagement = event.isActive && event.type !== "focus";

  return isEngaged(next) && !isReengagement
    ? next
    : { ...next, isDismissed: false };
}

function Sidebar({ className, menuItems }: Readonly<SidebarProps>) {
  const [state, dispatch] = useReducer(sidebarReducer, INITIAL_STATE);
  const isExpanded = isEngaged(state) && !state.isDismissed;
  const dismiss = () => dispatch({ type: "dismiss" });
  const currentUser = useCurrentUser();

  const menu = menuItems.map((item) => (
    <SidebarItem
      key={item.label}
      {...item}
      isMenuOpen={isExpanded}
      onNavigate={dismiss}
    />
  ));
  const handleBlurCapture = (event: FocusEvent<HTMLDivElement>) => {
    const nextTarget = event.relatedTarget;

    if (
      nextTarget instanceof Node &&
      event.currentTarget.contains(nextTarget)
    ) {
      return;
    }

    dispatch({ type: "focus", isActive: false });
  };
  const handleFocusCapture = () => {
    dispatch({ type: "focus", isActive: true });
  };
  const handleNavigationMenuOpenChange = (isOpen: boolean) => {
    dispatch({ type: "menu", isActive: isOpen });
  };

  return (
    <div
      id="sidebar"
      data-expanded={isExpanded}
      onMouseEnter={() => dispatch({ type: "pointer", isActive: true })}
      onMouseLeave={() => dispatch({ type: "pointer", isActive: false })}
      onFocusCapture={handleFocusCapture}
      onBlurCapture={handleBlurCapture}
      className={twMerge("relative z-50 h-full w-20 shrink-0", className)}
    >
      <div
        aria-hidden="true"
        data-testid="sidebar-backdrop"
        className={twMerge(
          "fixed inset-0 z-30 pointer-events-none bg-black/20",
          "transition-opacity duration-300 ease-in-out",
          isExpanded ? "opacity-100" : "opacity-0",
        )}
      />

      {/* opening the panel is a transform, not a width change: this window
          carries the background, slides by w-64 minus the w-20 rail, and clips
          whatever the edge has not reached yet. the inner wrapper cancels the
          slide with the same timing so the content never moves */}
      <div
        data-testid="sidebar-panel"
        className={twMerge(
          "bg-background-2 absolute inset-y-0 left-0 w-64 overflow-hidden z-50",
          "transition-transform duration-300 ease-out",
          isExpanded ? "translate-x-0 shadow-xl" : "-translate-x-44",
        )}
      >
        <div
          className={twMerge(
            "h-full w-64 transition-transform duration-300 ease-out",
            isExpanded ? "translate-x-0" : "translate-x-44",
          )}
        >
          <div
            data-testid="sidebar-content"
            className={twMerge(
              "flex h-full min-h-0 flex-col p-4",
              isExpanded ? "w-64" : "w-20",
            )}
          >
            <div className="mb-8 flex w-full items-center justify-start pl-2">
              <div className="flex shrink-0 items-center justify-center">
                <Logo size={32} />
              </div>
            </div>

            <ScrollArea
              orientation="vertical"
              className="scroll-area-hidden min-h-0 flex-1"
            >
              {currentUser?.organization && menu ? (
                <div className="flex flex-col items-stretch gap-2">{menu}</div>
              ) : null}
            </ScrollArea>

            {currentUser ? (
              <SidebarProfile
                currentUser={currentUser}
                isExpanded={isExpanded}
                onNavigationMenuOpenChange={handleNavigationMenuOpenChange}
                onCloseNavigationMenus={dismiss}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
